import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message
} from 'antd';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { apiGetCategory } from '@services/ManageDevices/DeviceServices';
import { apiCreateDMA, apiGetDMAById, apiUpdateDMA } from '@services/ManageDMA/DMAServices';

const { Title, Text } = Typography;

const AddDMA = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(undefined);

  /* ---- fetch helpers ---- */
  const fetchBranches = async () => {
    try {
      const res = await apiGetCategory({ type: 'CN', page: 1, size: 1000 });
      setBranchOptions((res.data?.data || []).map((b) => ({ label: b.name, value: b.value })));
    } catch (_) {
      // ignore
    }
  };

  const fetchGroups = async (branchCode) => {
    try {
      const res = await apiGetCategory({
        type: 'TQL',
        parent_level_1_id: branchCode || undefined,
        page: 1,
        size: 1000
      });
      setGroupOptions((res.data?.data || []).map((g) => ({ label: g.name, value: g.value })));
    } catch (_) {
      // ignore
    }
  };

  const fetchCustomers = async (keyword = '') => {
    setLoadingCustomers(true);
    try {
      const res = await apiGetAllCustomer({ page: 1, size: 50, keyWord: keyword || undefined });
      setCustomerOptions(
        (res.data?.data || []).map((c) => ({
          label: `[${c.code}] ${c.name}`,
          value: c.code,
          branchCode: c.branch_code,
          branch: c.branch,
          manageGroupCode: c.manage_group_code,
          manageGroup: c.manage_group
        }))
      );
    } catch (_) {
      // ignore
    } finally {
      setLoadingCustomers(false);
    }
  };

  const debouncedFetchCustomers = useCallback(debounce(fetchCustomers, 500), []);

  /* ---- init ---- */
  useEffect(() => {
    fetchBranches();
    fetchCustomers();
    fetchGroups();

    if (isEdit) {
      setLoadingData(true);
      apiGetDMAById(id)
        .then((res) => {
          const d = res.data?.data;
          if (d) {
            form.setFieldsValue({
              name: d.name,
              code: d.code,
              master_customer_code: (d.master_customer_code || []).map((code, i) => ({
                value: code,
                label: d.master_customer_name?.[i] || code
              })),
              customer_codes: d.customer_codes || [],
              branch_code: d.branch_code,
              manage_group_code: d.manage_group_code
            });
            setSelectedBranch(d.branch_code);
            fetchGroups(d.branch_code);
          }
        })
        .catch(() => message.error('Không tải được thông tin DMA'))
        .finally(() => setLoadingData(false));
    }
  }, [id]);

  useEffect(() => {
    fetchGroups(selectedBranch);
  }, [selectedBranch]);

  /* ---- handlers ---- */
  // When master meters change, auto-fill branch/group from the first selected meter
  // values is [{value, label}, ...] because labelInValue=true
  const handleMasterCustomerChange = (values) => {
    const firstCode = values?.[0]?.value;
    const first = customerOptions.find((c) => c.value === firstCode);
    if (first) {
      form.setFieldsValue({
        branch_code: first.branchCode,
        manage_group_code: first.manageGroupCode,
        _branch: first.branch,
        _manage_group: first.manageGroup
      });
      setSelectedBranch(first.branchCode);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const branchOpt = branchOptions.find((b) => b.value === values.branch_code);
      const groupOpt = groupOptions.find((g) => g.value === values.manage_group_code);
      // labelInValue gives [{value, label}, ...]
      const masterItems = values.master_customer_code || [];
      const masterCodes = masterItems.map((opt) => opt.value || opt);
      const masterNames = masterItems.map((opt) => opt.label || opt.value || opt);

      const payload = {
        name: values.name,
        code: values.code,
        master_customer_code: masterCodes,
        master_customer_name: masterNames,
        customer_codes: values.customer_codes || [],
        branch_code: values.branch_code,
        branch: branchOpt?.label || values._branch || values.branch_code,
        manage_group_code: values.manage_group_code,
        manage_group: groupOpt?.label || values._manage_group || values.manage_group_code
      };

      if (isEdit) {
        await apiUpdateDMA(id, payload);
        message.success('Cập nhật DMA thành công');
      } else {
        await apiCreateDMA(payload);
        message.success('Tạo DMA thành công');
      }
      navigate('/dma');
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.message || 'Có lỗi xảy ra';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loadingData}>
      <div style={{ width: '80%', paddingLeft: 20, paddingTop: 20 }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20
          }}>
          <div>
            <Title level={4} style={{ margin: 0, color: '#0051A9' }}>
              {isEdit ? 'Chỉnh sửa DMA' : 'Tạo mới DMA'}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {isEdit ? 'Cập nhật thông tin vùng đo DMA' : 'Thêm mới vùng đo DMA vào hệ thống'}
            </Text>
          </div>
          <Space>
            <Button icon={<CloseOutlined />} onClick={() => navigate('/dma')}>
              Huỷ
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSubmit}
              style={{ backgroundColor: '#0051A9' }}>
              {isEdit ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </Space>
        </div>

        <Form form={form} layout="vertical">
          {/* hidden helpers */}
          <Form.Item name="_branch" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="_manage_group" hidden>
            <Input />
          </Form.Item>

          {/* --- Thông tin cơ bản --- */}
          <Card
            size="small"
            title={
              <Text strong style={{ color: '#0051A9' }}>
                Thông tin cơ bản
              </Text>
            }
            style={{ marginBottom: 16 }}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Tên DMA"
                  name="name"
                  rules={[{ required: true, message: 'Vui lòng nhập tên DMA' }]}>
                  <Input placeholder="Nhập tên DMA" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Mã DMA" name="code" tooltip="Tự động sinh theo dạng DMA_0001">
                  <Input placeholder={isEdit ? '' : 'Tự động sinh'} disabled />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* --- Đồng hồ --- */}
          <Card
            size="small"
            title={
              <Text strong style={{ color: '#0051A9' }}>
                Đồng hồ
              </Text>
            }
            style={{ marginBottom: 16 }}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Đồng hồ tổng"
                  name="master_customer_code"
                  rules={[{ required: true, message: 'Vui lòng chọn ít nhất một đồng hồ tổng' }]}
                  tooltip="Nhà máy và Tổ quản lý tự động điền theo đồng hồ tổng đầu tiên">
                  <Select
                    showSearch
                    filterOption={false}
                    onSearch={debouncedFetchCustomers}
                    loading={loadingCustomers}
                    mode="multiple"
                    labelInValue
                    placeholder="Tìm và chọn các đồng hồ tổng"
                    onChange={handleMasterCustomerChange}
                    allowClear
                    options={customerOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Đồng hồ khách hàng"
                  name="customer_codes"
                  tooltip="Các đồng hồ khách hàng thuộc DMA này">
                  <Select
                    showSearch
                    filterOption={false}
                    onSearch={debouncedFetchCustomers}
                    loading={loadingCustomers}
                    mode="multiple"
                    placeholder="Tìm và chọn các khách hàng"
                    allowClear
                    options={customerOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* --- Phân vùng quản lý --- */}
          <Card
            size="small"
            title={
              <Text strong style={{ color: '#0051A9' }}>
                Phân vùng quản lý
              </Text>
            }
            style={{ marginBottom: 16 }}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item label="Nhà máy" name="branch_code">
                  <Select
                    placeholder="Chọn Nhà máy"
                    options={branchOptions}
                    allowClear
                    showSearch
                    filterOption={(input, opt) =>
                      (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(val) => {
                      setSelectedBranch(val);
                      form.setFieldsValue({ manage_group_code: undefined });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Tổ quản lý" name="manage_group_code">
                  <Select
                    placeholder="Chọn tổ quản lý"
                    options={groupOptions}
                    allowClear
                    showSearch
                    filterOption={(input, opt) =>
                      (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </div>
    </Spin>
  );
};

export default AddDMA;
